import { firebaseConfig } from "./config";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import "firebase/compat/storage";
import "firebase/compat/auth";
import { NativelyInfo } from "natively/dist";
import { getStorage } from "firebase/storage";

export const firebaseApp = firebase.initializeApp(firebaseConfig);

const db = firebaseApp.firestore();

db.settings({ experimentalAutoDetectLongPolling: true, ignoreUndefinedProperties: true });

export const storage = firebaseApp.storage();

export const newStorage = getStorage();

export const login = (email: string, password: string) =>
  firebase.auth().signInWithEmailAndPassword(email, password);

export const loginWithGoogle = (nativelyInfo: NativelyInfo) => {
  const provider = new firebase.auth.GoogleAuthProvider();

  const browserInfo = nativelyInfo.browserInfo();

  // if (browserInfo.isAndroidApp || browserInfo.isIOSApp) {
  //   return firebase.auth().signInWithRedirect(provider);
  // } else {
  return firebase.auth().signInWithPopup(provider);
  // }
};

export const loginWithFaceBook = () => {
  const provider = new firebase.auth.FacebookAuthProvider();
  return firebase.auth().signInWithPopup(provider);
};

export const loginWithTwitter = () => {
  const provider = new firebase.auth.TwitterAuthProvider();
  return firebase.auth().signInWithPopup(provider);
};

// Create an email/password auth account and immediately send the verification
// email. We deliberately do NOT write a Firestore user document here: the real
// user collection is `dashboard-users-v2` keyed by EMAIL (not the `users`
// collection keyed by uid that this used to write), and provisioning is handled
// by FirestoreContext once the user is verified. Both the server and the
// Firestore rules reject an unverified email, so the account is inert until the
// user clicks the link.
export const register = async (email: string, password: string) => {
  const credential = await firebase.auth().createUserWithEmailAndPassword(email, password);
  await credential.user?.sendEmailVerification();
  return credential;
};

// Re-send the verification email to the currently signed-in user.
export const sendVerificationEmail = async () => {
  await firebase.auth().currentUser?.sendEmailVerification();
};

// Change the signed-in user's password. Firebase requires a recent login for
// this security-sensitive op; if the session is stale it throws
// `auth/requires-recent-login` and the caller must reauthenticate.
export const changePassword = async (newPassword: string) => {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error("No authenticated user");
  await user.updatePassword(newPassword);
};

// Reauthenticate the signed-in email/password user with their current password.
// Needed before change-password when the session is old (auth/requires-recent-login).
export const reauthenticateWithPassword = async (currentPassword: string) => {
  const user = firebase.auth().currentUser;
  if (!user || !user.email) throw new Error("No authenticated user");
  const cred = firebase.auth.EmailAuthProvider.credential(user.email, currentPassword);
  await user.reauthenticateWithCredential(cred);
};

// True if the signed-in account already has an email/password credential.
export const currentUserHasPasswordProvider = (): boolean =>
  (firebase.auth().currentUser?.providerData ?? []).some((p) => p?.providerId === "password");

// True if the signed-in account has a linked Google credential. Used by the
// security settings to offer Google reauth as the identity proof when the user
// forgot their current password (reauthenticateWithPopup on the CURRENT user
// throws auth/user-mismatch if a different Google account is picked, so this
// can never move the session to another account).
export const currentUserHasGoogleProvider = (): boolean =>
  (firebase.auth().currentUser?.providerData ?? []).some((p) => p?.providerId === "google.com");

// Reauthenticate a Google-signed-in user via the Google popup. Needed before a
// security-sensitive op (like linking a password) when the session is stale.
export const reauthenticateWithGoogle = async () => {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error("No authenticated user");
  await user.reauthenticateWithPopup(new firebase.auth.GoogleAuthProvider());
};

// Add an email/password credential to the CURRENT account (e.g. a Google user
// who also wants password login). We link against the account's own email so
// the two providers stay on one identity; the email is already verified by
// Google, so password login works immediately with no verification step. If the
// session is stale Firebase throws `auth/requires-recent-login` — the caller
// reauthenticates (Google popup) and retries.
export const linkPasswordToCurrentUser = async (newPassword: string) => {
  const user = firebase.auth().currentUser;
  if (!user || !user.email) throw new Error("No authenticated user");
  const cred = firebase.auth.EmailAuthProvider.credential(user.email, newPassword);
  await user.linkWithCredential(cred);
};

export const logout = async () => {
  await firebase.auth().signOut();
  window.location.reload();
};

export const resetPassword = async (email: string) => {
  await firebase.auth().sendPasswordResetEmail(email);
};

export { db, firebase };
